import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { EditarNumberTicketsComponent } from 'src/app/file/ticketeria/table-expand/editar-number-tickets/editar-number-tickets.component';
import { FileService } from 'src/app/services/api/file.service';
import { MassOsItineraryService, MassOsTicket } from 'src/entities/file';
import { DialogEditTicketAmountComponent } from 'src/features/ticketeria/edit-ticket-amount/ui/dialog-edit-ticket-amount/dialog-edit-ticket-amount.component';
import { buildEditTicketAmountDialogConfig } from 'src/features/ticketeria/edit-ticket-amount/ui/dialog-edit-ticket-amount/edit-ticket-amount-dialog.config';

@Component({
  selector: 'app-mass-os-ticket-expand',
  templateUrl: './mass-os-ticket-expand.component.html',
  styleUrls: ['./mass-os-ticket-expand.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MassOsTicketExpandComponent implements OnInit, OnDestroy {
  @Input() service!: MassOsItineraryService;
  /** Tickets seleccionados por servicio (serviceId → ticketIds), vive en el padre. */
  @Input() selectedTickets: Record<string, string[]> = {};

  @Output() ticketToggle = new EventEmitter<{
    serviceId: string;
    ticketId: string;
    checked: boolean;
  }>();

  readonly displayedColumns = [
    'Check',
    'createdAt',
    'ticketTipos',
    'monto',
    'estadoTicket',
    'estadoOS',
    'numeroOS',
    'fechaOS'
  ];

  tickets: MassOsTicket[] = [];
  loading = false;
  loaded = false;
  /** true si el servicio tiene tickets pero todos ya cuentan con OS. */
  allTicketsHaveOrder = false;

  private sub?: Subscription;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly fileService: FileService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Cada reapertura de la sub-tabla vuelve a consultar el servicio de tickets
    // para traer el estado más reciente; la selección vive en el padre
    // (selectedTickets), por lo que el re-fetch no pierde los checkboxes.
    this.loadTickets();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private loadTickets(): void {
    const idItinerary = this.service?.idItinerary ?? '';
    const serviceId = this.service?.serviceId ?? this.service?.id ?? '';
    if (!idItinerary || !serviceId) {
      this.loading = false;
      this.loaded = true;
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.loaded = false;
    this.cdr.markForCheck();

    this.sub = this.fileService
      .getTicketeriaTicketsByService({ serviceId, idItinerary })
      .subscribe({
        next: (data: any) => {
          const rawTickets = data?.getTicketeriaTicketsByService?.tickets ?? [];
          const allTickets: MassOsTicket[] = rawTickets.map((raw: any) =>
            this.decorateTicket(raw)
          );
          // Solo se muestran los tickets pendientes de OS: los que ya tienen
          // order quedan ocultos para no confundir en la selección.
          const pendingTickets = allTickets.filter(
            (ticket) => !this.hasOrder(ticket)
          );
          this.tickets = pendingTickets;
          this.allTicketsHaveOrder =
            allTickets.length > 0 && pendingTickets.length === 0;
          if (this.service) {
            this.service.tickets = pendingTickets;
          }
          this.loading = false;
          this.loaded = true;
          this.cdr.markForCheck();
        },
        error: () => {
          this.tickets = [];
          this.allTicketsHaveOrder = false;
          if (this.service) {
            this.service.tickets = [];
          }
          this.loading = false;
          this.loaded = true;
          this.cdr.markForCheck();
        }
      });
  }

  private decorateTicket(raw: any): MassOsTicket {
    const order = raw?.order ?? null;
    const paxTickets = raw?.paxTickets ?? null;
    const objTarifa = raw?.objTarifa ?? null;

    return {
      id: String(raw?.id ?? ''),
      createdAt: raw?.createdAt ?? null,
      paxTickets,
      objTarifa,
      ticketTipos: paxTickets ? this.ticketTiposFromPax(paxTickets) : [],
      total: raw?.total ?? null,
      moneda: this.normalizeMoneda(
        raw?.moneda ?? objTarifa?.monedaTarifa ?? this.service?.moneda
      ),
      estadoTicket: this.normalizeEstadoTicket(raw?.estadoTicket),
      hasDocument: raw?.hasDocument ?? false,
      hasInvalidFechaOperacion: Boolean(raw?.hasInvalidFechaOperacion),
      order,
      checked: false
    };
  }

  private ticketTiposFromPax(
    paxTickets: any
  ): Array<{ cantidad: string; tipo: string }> {
    const base: Array<{
      nombreDB: string;
      tipo: string;
      cantidad: number;
    }> = [
      { nombreDB: 'ticketAdulto', tipo: 'Ticket Adulto', cantidad: 0 },
      { nombreDB: 'ticketEstudiante', tipo: 'Ticket Estudiante', cantidad: 0 },
      { nombreDB: 'ticketNacional', tipo: 'Ticket Nacional', cantidad: 0 },
      { nombreDB: 'ticketNino', tipo: 'Ticket Niño', cantidad: 0 }
    ];
    for (const item of base) {
      const value = Number(paxTickets?.[item.nombreDB] ?? 0);
      item.cantidad = Number.isFinite(value) ? value : 0;
    }
    return base
      .filter((b) => b.cantidad > 0)
      .map((b) => ({ tipo: b.tipo, cantidad: String(b.cantidad) }));
  }

  normalizeMoneda(moneda: string | null | undefined): string {
    if (!moneda) return 'S/';
    const value = String(moneda).toUpperCase().trim();
    if (
      value === 'D' ||
      value === '$' ||
      value === 'USD' ||
      value.includes('DOLAR')
    ) {
      return '$';
    }
    return 'S/';
  }

  normalizeEstadoTicket(estado: string | null | undefined): string {
    if (!estado) return '';
    const key = estado.toUpperCase().replace(/\s+/g, '_');
    if (key === 'CREATED') return 'CREADO';
    if (key === 'ENPROCESO') return 'EN_PROCESO';
    return key;
  }

  /* ================= Selección ================= */

  isTicketChecked(ticketId: string): boolean {
    return (this.selectedTickets[this.service?.id ?? ''] ?? []).includes(
      ticketId
    );
  }

  /** El ticket ya tiene una OS o NP generada. */
  hasOrder(ticket: MassOsTicket): boolean {
    return Boolean(
      ticket?.order?.orderId ||
        ticket?.order?.orderNumber ||
        (ticket as any)?.purchaseNoteId ||
        (ticket as any)?.purchaseNote?.purchaseNoteId
    );
  }

  canSelectTicket(ticket: MassOsTicket): boolean {
    if (!(this.service?.total != null && Number(this.service.total) > 0)) {
      return false;
    }
    if (this.hasOrder(ticket)) {
      return false;
    }
    return this.normalizeEstadoTicket(ticket?.estadoTicket) === 'CREADO';
  }

  /** Motivo por el que un ticket no se puede seleccionar (para el tooltip). */
  getTicketDisableReason(ticket: MassOsTicket): string {
    const total = this.service?.total;
    if (!(total != null && Number(total) > 0)) {
      return 'El servicio no tiene monto asignado';
    }
    if (this.hasOrder(ticket)) {
      return 'Este ticket ya tiene una OS o Nota de Pedido generada';
    }
    const estado = this.normalizeEstadoTicket(ticket?.estadoTicket);
    if (estado !== 'CREADO') {
      return `El ticket no está en estado CREADO (${estado || 'sin estado'})`;
    }
    return '';
  }

  onTicketToggle(ticket: MassOsTicket, checked: boolean): void {
    if (!ticket.id || !this.service) {
      return;
    }
    this.ticketToggle.emit({
      serviceId: this.service.id,
      ticketId: ticket.id,
      checked
    });
  }

  /* ================= Ediciones ================= */

  canEditAmount(ticket: MassOsTicket): boolean {
    return this.canSelectTicket(ticket);
  }

  /** Con OS no preliminar no se cambia este ticket. Preliminar sí. */
  private orderLocksTicketQuantity(ticket: MassOsTicket): boolean {
    const hasOrder = Boolean(
      ticket?.order?.orderId || ticket?.order?.orderNumber
    );
    if (!hasOrder) {
      return false;
    }
    const status = String(ticket?.order?.orderStatus || '')
      .trim()
      .toUpperCase();
    return status !== 'PRELIMINAR';
  }

  canEditNumberTickets(ticket: MassOsTicket): boolean {
    if (
      this.orderLocksTicketQuantity(ticket) ||
      Boolean(
        (ticket as any)?.purchaseNoteId ||
          (ticket as any)?.purchaseNote?.purchaseNoteId
      )
    ) {
      return false;
    }
    if (!ticket?.objTarifa?.tarifaTicket) {
      return false;
    }
    const status = String(ticket?.order?.orderStatus || '')
      .trim()
      .toUpperCase();
    if (status === 'PRELIMINAR') {
      return true;
    }
    return this.normalizeEstadoTicket(ticket?.estadoTicket) === 'CREADO';
  }

  editTicketAmount(ticket: MassOsTicket): void {
    if (!this.service) return;
    const dialogRef = this.dialog.open(
      DialogEditTicketAmountComponent,
      buildEditTicketAmountDialogConfig({
        ticketId: ticket.id ?? '',
        serviceId: this.service.serviceId ?? this.service.id,
        currentTotal: ticket.total ?? null,
        tickets: this.tickets,
        tipoCambio: ticket?.objTarifa?.tipoCambio ?? null,
        moneda: ticket?.objTarifa?.monedaTarifa ?? ticket?.moneda ?? null,
        tipoMoneda: ticket?.objTarifa?.monedaTarifa ?? ticket?.moneda ?? null,
        objTarifa: ticket?.objTarifa ?? null,
        servicioTipoCambio: ticket?.objTarifa?.tipoCambio ?? null,
        nombreServicio: this.service.nombreServicio
      })
    );

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        this.reloadTickets();
      }
    });
  }

  editTicketNumber(ticket: MassOsTicket): void {
    if (!this.service) return;
    const dialogRef = this.dialog.open(EditarNumberTicketsComponent, {
      width: '1000px',
      autoFocus: false,
      data: {
        id: ticket.id,
        servicioId: this.service.serviceId ?? this.service.id,
        moneda: ticket?.objTarifa?.monedaTarifa ?? ticket?.moneda ?? null,
        tipoCambio: ticket?.objTarifa?.tipoCambio ?? null,
        paxTickets: ticket?.paxTickets ?? {},
        tarifaTicket: ticket?.objTarifa?.tarifaTicket ?? {},
        tickets: this.tickets.map((t) => ({
          id: t.id,
          monto: t.total
        }))
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        this.reloadTickets();
      }
    });
  }

  private reloadTickets(): void {
    this.sub?.unsubscribe();
    this.loadTickets();
  }

  trackTicket = (_index: number, ticket: MassOsTicket): string =>
    ticket.id ?? `${_index}`;
}
